import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { useAppStore, type LlmStatus } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { signOut } from "../../lib/auth/msal";
import { resolveLlmConfigs } from "../../lib/fs/config";
import logo from "../../assets/lens_logo_light_mode.svg";
import logoDark from "../../assets/lens_logo_with_lens_effect.svg";

const STATUS_DOT: Record<LlmStatus, { color: string; title: string }> = {
  idle:  { color: "bg-slate-300 dark:bg-slate-600",  title: "Not used this session" },
  ok:    { color: "bg-emerald-400",                  title: "Last request succeeded" },
  quota: { color: "bg-amber-400",                    title: "Quota / rate limit reached" },
  error: { color: "bg-red-400",                      title: "Last request failed" },
};


function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Header() {
  const t = useT();
  const user = useAppStore((s) => s.user);
  const lensUser = useAppStore((s) => s.lensUser);
  const [, navigate] = useLocation();
  const language = useAppStore((s) => s.language);
  const setLanguage = useAppStore((s) => s.setLanguage);
  const theme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const setAuth = useAppStore((s) => s.setAuth);

  const activeLlmId = useAppStore((s) => s.activeLlmId);
  const setActiveLlm = useAppStore((s) => s.setActiveLlm);
  const llmStatus = useAppStore((s) => s.llmStatus);
  const settings = useAppStore((s) => s.settings);

  const llmConfigs = settings ? resolveLlmConfigs(settings) : [];
  const activeConfig = llmConfigs.find((c) => c.id === activeLlmId) ?? llmConfigs[0];

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);

  const displayName = lensUser?.display_name ?? user?.displayName ?? "User";
  const initials = getInitials(displayName);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (modelRef.current && !modelRef.current.contains(event.target as Node)) {
        setModelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleSignOut() {
    await signOut();
    setAuth("signed_out");
  }


  return (
    <>
    <header className="sticky top-0 z-40 w-full border-b border-slate-200/80 dark:border-slate-700/50 bg-canvas/95 dark:bg-[#0e1520]/95 backdrop-blur-md shadow-xs">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-10">
        
        {/* Left: Logo */}
        <div className="flex flex-1 items-center">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 transition-transform active:scale-95 cursor-pointer focus:outline-none"
            aria-label="Lens Home"
          >
            {/* Logo sits in a canvas-coloured chip so the baked-in SVG bg is always seamless */}
            <span className="rounded-lg overflow-hidden bg-canvas dark:bg-[#0e1520] shrink-0 flex items-center justify-center h-10 w-24">
              <img src={theme === "dark" ? logoDark : logo} alt="Lens Logo" className="h-14 w-auto object-cover scale-125 block" />
            </span>
          </button>
        </div>

        {/* Center: spacer — navigation moved to left sidebar */}
        <div className="flex-1" />

        {/* Right: Language & User Profile */}
        <div className="flex flex-1 items-center justify-end gap-5">

          {/* Model Switcher — shown when more than one LLM config is available */}
          {llmConfigs.length > 1 && activeConfig && (
            <div className="relative" ref={modelRef}>
              <button
                onClick={() => setModelOpen(!modelOpen)}
                className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/80 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80 transition-colors shadow-2xs cursor-pointer focus:outline-none"
                aria-expanded={modelOpen}
                aria-haspopup="true"
                title="Switch AI model"
              >
                <span
                  className={`h-2 w-2 rounded-full flex-shrink-0 ${STATUS_DOT[llmStatus[activeConfig.id] ?? "idle"].color}`}
                  title={STATUS_DOT[llmStatus[activeConfig.id] ?? "idle"].title}
                />
                <span className="max-w-[120px] truncate">{activeConfig.label}</span>
                <span className="text-slate-400 text-2xs">▼</span>
              </button>

              {modelOpen && (
                <div className="absolute right-0 mt-2 w-60 origin-top-right rounded-xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-1.5 shadow-lg ring-1 ring-black/5 dark:ring-white/5 focus:outline-none animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="px-3 py-2 text-4xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-700 mb-1">
                    AI Model
                  </div>
                  <div className="space-y-0.5">
                    {llmConfigs.map((cfg) => {
                      const status = llmStatus[cfg.id] ?? "idle";
                      const dot = STATUS_DOT[status];
                      const isSelected = cfg.id === activeLlmId;
                      return (
                        <button
                          key={cfg.id}
                          onClick={() => { setActiveLlm(cfg.id); setModelOpen(false); }}
                          className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs transition-colors cursor-pointer text-left focus:outline-none ${
                            isSelected
                              ? "bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-bold"
                              : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60 hover:text-slate-900 dark:hover:text-slate-100"
                          }`}
                        >
                          <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dot.color}`} title={dot.title} />
                          <span className="flex-1 truncate">{cfg.label}</span>
                          <span className="text-2xs text-slate-400 dark:text-slate-500 capitalize">{cfg.provider}</span>
                          {isSelected && <span className="text-emerald-600 font-bold text-xs">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dark / Light Mode Toggle */}
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex items-center justify-center h-8 w-8 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/80 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer focus:outline-none shadow-2xs"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* EN / NL Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800/80 p-0.5 rounded-lg border border-slate-200/50 dark:border-slate-600">
            {(["en", "nl"] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-md uppercase transition-all duration-150 cursor-pointer ${
                  language === lang
                    ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-xs border border-slate-200/30 dark:border-slate-600/30"
                    : "text-slate-500 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                }`}
              >
                {lang}
              </button>
            ))}
          </div>

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-linear-to-br from-slate-800 to-slate-950 text-white shadow-sm ring-2 ring-slate-100 dark:ring-slate-700 transition-transform hover:scale-105 active:scale-95 cursor-pointer focus:outline-none"
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              <span className="text-xs font-bold tracking-wider">{initials}</span>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl border border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-1.5 shadow-lg ring-1 ring-black/5 dark:ring-white/5 focus:outline-none animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="border-b border-slate-100 dark:border-slate-700 px-3 py-2.5 mb-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {displayName}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                    {lensUser?.email ?? user?.email}
                  </p>
                </div>

                {[
                  { path: "/consolidation", label: language === "nl" ? "Profiel Consolideren" : "Consolidate Profile" },
                  { path: "/settings",      label: t("nav.settings") },
                ].map(({ path, label }) => (
                  <button
                    key={path}
                    onClick={() => { navigate(path); setDropdownOpen(false); }}
                    className="flex w-full items-center rounded-lg px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors cursor-pointer text-left focus:outline-none"
                  >
                    {label}
                  </button>
                ))}

                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50/60 dark:hover:bg-red-900/20 transition-colors cursor-pointer text-left focus:outline-none"
                >
                  {t("nav.sign_out")}
                </button>
              </div>
            )}
          </div>

        </div>

      </div>

    </header>

</>
  );
}
